import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Loader2, ArrowRight, ArrowLeft, Check, MapPin, Sparkles,
  Store, Target, DollarSign, Handshake, Plug, CheckCircle2, X, Plus,
} from "lucide-react";
import { sr, MEDIA_CHANNELS } from "../../lib/sr";
import { useCurrency } from "../../lib/useCurrency";
import DateField from "../../components/smartreach/DateField";
import ChannelPicker from "../../components/smartreach/ChannelPicker";
import AdvancedTargeting, { targetingSummary } from "../../components/smartreach/AdvancedTargeting";
import MoviePromoCapture from "../../components/smartreach/MoviePromoCapture";
import IndustryIntake, { PlaybookCard } from "../../components/smartreach/IndustryIntake";
import { GeographyPicker } from "../../components/smartreach/RadioPlanner";

const GOALS = [
  { key: "visits", label: "Store visits" }, { key: "calls", label: "Phone calls" },
  { key: "bookings", label: "Bookings / reservations" }, { key: "leads", label: "Qualified leads" },
  { key: "sales", label: "Online sales" }, { key: "events", label: "Event turnout" },
  { key: "repeat", label: "Repeat customers" },
];
const MODES = [
  { key: "SELF_SERVICE", label: "Self-Service", desc: "AI builds the plan; you approve and launch." },
  { key: "AI_ASSISTED", label: "AI-Assisted", desc: "AI builds & optimizes; a specialist reviews exceptions." },
  { key: "PROFESSIONAL", label: "Professional Service", desc: "An assigned team runs everything for you." },
];
const AUTH = [
  { key: "RECOMMEND_ONLY", label: "Recommend only", desc: "Every material change needs your approval." },
  { key: "GUARDED_AUTOPILOT", label: "Guarded autopilot", desc: "AI adjusts pacing, bids & rotation within limits." },
  { key: "MANAGED_AUTOPILOT", label: "Managed autopilot", desc: "Team & AI act within your approved scope." },
];
const OPERATING = [
  { key: "physical", label: "At a physical location", hint: "Walk-in customers in a local area" },
  { key: "online", label: "Online", hint: "Website, store or bookings — no walk-in" },
  { key: "hybrid", label: "Both online and in person", hint: "Local presence plus online" },
];
const PROMOTE = [
  { key: "products", label: "Products" },
  { key: "services", label: "Services or subscriptions" },
  { key: "events", label: "Events" },
  { key: "business", label: "The whole business" },
];
const CONV_LOC = [
  { key: "owned_site", label: "My website" },
  { key: "checkout", label: "Online checkout" },
  { key: "booking", label: "Booking system" },
  { key: "external_seller", label: "External seller / marketplace" },
];
const STEP_META = [
  { icon: Store, t: "Who are you growing?" }, { icon: Target, t: "Choose your outcome" },
  { icon: DollarSign, t: "Define your economics" }, { icon: Handshake, t: "Choose your service mode" },
  { icon: Plug, t: "Connect your data" }, { icon: Sparkles, t: "Generate your growth plan" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ economics: {}, connections: {} });
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { sr.onboardingSchema().then(setSchema).catch(() => setSchema({})); }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await sr.onboardingGet();
        const s = r.state || {};
        let merged = { economics: {}, connections: {}, ...(s.data || {}) };
        // Prefill from the already-selected client's saved business profile so we don't
        // ask an acting-as staffer (or a returning client) to search for the business again.
        if (!merged.profile?.name) {
          try {
            const pr = await sr.profileGet();
            const p = pr.profile;
            if (p?.name) {
              merged = {
                ...merged,
                profile: {
                  name: p.name, category: p.category, website: p.website, phone: p.phone,
                  address: p.address, description: p.description, hours: p.hours,
                  place_id: p.place_id, lat: p.lat, lng: p.lng,
                  source: p.provenance || (p.place_id ? "google_places" : "existing"),
                },
              };
            }
          } catch { /* no existing profile — fall back to search */ }
        }
        setStep(s.completed ? 6 : (s.step || 1));
        setData(merged);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const persist = (nextStep, patch = {}, completed = false) => {
    const merged = { ...data, ...patch };
    setData(merged);
    sr.onboardingSave(nextStep, merged, completed).catch(() => {});
  };
  const go = (n, patch) => { persist(n, patch); setStep(n); };

  if (loading || !schema) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span className="sr-eyebrow">Setup · Step {step} of 6</span>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app")}>Save & continue later</button>
      </div>
      <div className="sr-progress" style={{ marginBottom: 24 }}><div style={{ width: `${(step / 6) * 100}%` }} /></div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 22 }}>
        {React.createElement(STEP_META[step - 1].icon, { size: 26, color: "var(--blue)" })}
        <h1 style={{ fontSize: 28 }}>{STEP_META[step - 1].t}</h1>
      </div>

      {step === 1 && <Step1 data={data} schema={schema} onNext={(patch) => go(2, patch)} saving={saving} setSaving={setSaving} />}
      {step === 2 && <Step2 data={data} schema={schema} onBack={() => setStep(1)} onNext={(patch) => go(3, patch)} />}
      {step === 3 && <Step3 data={data} onBack={() => setStep(2)} onNext={(patch) => go(4, patch)} />}
      {step === 4 && <Step4 data={data} onBack={() => setStep(3)} onNext={(patch) => go(5, patch)} />}
      {step === 5 && <Step5 data={data} onBack={() => setStep(4)} onNext={() => go(6, {})} />}
      {step === 6 && <Step6 data={data} schema={schema} onBack={() => setStep(5)} persist={persist} nav={nav} />}
    </div>
  );
}

const Nav = ({ onBack, onNext, nextLabel = "Continue", disabled }) => (
  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28 }}>
    {onBack ? <button className="sr-btn sr-btn-ghost" onClick={onBack} data-testid="ob-back"><ArrowLeft size={16} /> Back</button> : <span />}
    {onNext && <button className="sr-btn sr-btn-primary" onClick={onNext} disabled={disabled} data-testid="ob-next">{nextLabel} <ArrowRight size={16} /></button>}
  </div>
);

function Step1({ data, onNext, saving, setSaving, schema }) {
  const [q, setQ] = useState(data.profile?.name || "");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(data.profile || null);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState("");
  const [role, setRole] = useState(data.role || "owner");
  const [ownerType, setOwnerType] = useState(data.owner_type || "single_unit");
  const [smsConsent, setSmsConsent] = useState(false);
  const [operatingModel, setOperatingModel] = useState(data.operating_model || "physical");
  const [promoteTypes, setPromoteTypes] = useState(data.promote_types || ["business"]);
  const [wsUrl, setWsUrl] = useState(data.website || "");
  const [wsBusy, setWsBusy] = useState(false);
  const [territories, setTerritories] = useState((data.sell_territories || []).join(", "));
  const [currency, setCurrency] = useState(data.reporting_currency || "USD");
  const [tz, setTz] = useState(data.timezone || "");
  const [conversionLoc, setConversionLoc] = useState(data.conversion_location || "owned_site");
  const [moviePromo, setMoviePromo] = useState(data.movie_promo || null);
  const [industry, setIndustry] = useState(data.industry || "");
  const [intake, setIntake] = useState(data.intake || {});
  const [prefilling, setPrefilling] = useState(false);

  // The chosen industry drives the whole step: which questions to ask, whether the
  // generic e-commerce block applies, and the default sell model.
  const wf = schema?.workflows?.[industry] || schema?.workflows?.default || {};
  const askOM = wf.ask_operating_model !== false;
  const askPromote = wf.ask_promote_types !== false;
  const effOM = askOM ? operatingModel : (wf.default_operating_model || "physical");
  const online = effOM === "online";
  const showCommerce = wf.commerce === true && effOM !== "physical";
  const isMovieDist = industry === "movie-distribution";

  const aiPrefill = async () => {
    const nm = picked?.name || q;
    if (!nm && !wsUrl) { toast.error("Import or enter your business first"); return; }
    setPrefilling(true);
    try {
      const r = await sr.suggestIntake({ name: nm, website: picked?.website || wsUrl, category: picked?.category, summary: picked?.summary || picked?.description, operating_model: effOM });
      if (r.industry) setIndustry(r.industry);
      setIntake((prev) => ({ ...(prev || {}), ...(r.intake || {}) }));
      toast.success("AI pre-filled your industry & details — please review.");
    } catch { toast.error("Could not auto-detect"); } finally { setPrefilling(false); }
  };

  const togglePromote = (k) => setPromoteTypes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true); setNote("");
    try {
      const r = await sr.importBusiness(q);
      setResults(r.results || []);
      if (!r.configured) setNote("Business lookup is pending external activation — you can enter details manually below.");
      else if (!r.results?.length) setNote("No matches found — refine your search or enter details manually.");
    } catch { toast.error("Search failed"); } finally { setSearching(false); }
  };
  const pick = (p) => { setPicked(p); setResults([]); };

  const importSite = async () => {
    if (!wsUrl.trim()) { toast.error("Enter your website URL"); return; }
    setWsBusy(true);
    try {
      const r = await sr.importWebsite(wsUrl.trim());
      const f = r.fields || {};
      setPicked({ name: f.name || wsUrl, category: f.category, website: f.website || wsUrl, summary: f.description, logo_url: f.logo_url, source: "website_import" });
      toast.success("Imported — review and edit below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't import that website — enter details manually"); }
    finally { setWsBusy(false); }
  };

  const cont = async () => {
    const pts = isMovieDist ? ["movies"] : promoteTypes;
    if (!isMovieDist && !pts.length) { toast.error("Pick at least one thing you promote"); return; }
    if (!industry) { toast.error("Pick your industry"); return; }
    if (!picked?.name) { toast.error(online ? "Import your website or enter your business name" : "Search and select your business, or enter a name"); return; }
    setSaving(true);
    try {
      const body = {
        name: picked.name, category: picked.category, website: picked.website || wsUrl, phone: picked.phone,
        address: online ? undefined : picked.address, description: picked.summary || picked.description, hours: picked.hours,
        place_id: picked.place_id, lat: picked.lat, lng: picked.lng, role, provenance: picked.source || "manual",
        sms_consent: smsConsent,
        ...(industry ? { industry } : {}),
        ...(intake && Object.keys(intake).length ? { intake } : {}),
      };
      await sr.profileSave(body);
      if (!online) {
        const locs = await sr.locations();
        if (!locs.locations?.length && picked.address) {
          const loc = await sr.createLocation({ name: picked.name, address: picked.address, lat: picked.lat, lng: picked.lng, place_id: picked.place_id, category: picked.category });
          if (loc.location?.place_id) await sr.verifyLocation(loc.location.id);
        }
      }
      // Persist the captured movie (if a specific movie was chosen) so it lands in the Catalog once.
      let moviePatch = {};
      if (isMovieDist && moviePromo?.mode === "movie" && moviePromo.fields?.title?.trim()) {
        try {
          const r = await sr.saveMoviePromo({ ...moviePromo.fields, item_id: moviePromo.itemId || undefined });
          moviePatch = { movie_promo: { ...moviePromo, itemId: r.item.id } };
        } catch { moviePatch = { movie_promo: moviePromo }; }
      } else if (isMovieDist && moviePromo) {
        moviePatch = { movie_promo: moviePromo };
      }
      onNext({
        profile: body, owner_type: online ? "single_unit" : ownerType,
        operating_model: effOM, promote_types: pts, website: picked.website || wsUrl,
        ...(industry ? { industry } : {}),
        ...(intake && Object.keys(intake).length ? { intake } : {}),
        ...moviePatch,
        ...(showCommerce ? {
          sell_territories: territories ? territories.split(",").map((s) => s.trim()).filter(Boolean) : [],
          reporting_currency: currency, timezone: tz, conversion_location: conversionLoc,
        } : {}),
      });
    } catch { toast.error("Could not save your business"); } finally { setSaving(false); }
  };

  return (
    <div>
      {/* Industry first — it drives everything below */}
      <div data-testid="ob-industry-intake">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={aiPrefill} disabled={prefilling} data-testid="ob-ai-prefill">
            {prefilling ? <Loader2 size={14} className="sr-spin" /> : <Sparkles size={14} />} Suggest with AI
          </button>
        </div>
        <IndustryIntake schema={schema} sections={isMovieDist ? ["industry"] : ["industry", "questions"]} idPrefix="ob"
          sellType={effOM} includeBaseQuestions={false}
          industry={industry} onIndustry={setIndustry}
          intake={intake} onIntake={setIntake} />
        {isMovieDist && (
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="ob-movie-intake-note">
            Movie details (title, rating, release, trailer &amp; poster) are captured below — no need to repeat them.
          </p>
        )}
      </div>

      {/* How does this business sell? — hidden for industries where it doesn't apply (e.g. Movie Distribution) */}
      {askOM && (
        <div className="sr-field" data-testid="ob-operating-model" style={{ marginTop: 16 }}>
          <label className="sr-label">How does this business sell to customers?</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginTop: 6 }}>
            {OPERATING.map((o) => {
              const on = operatingModel === o.key;
              return (
                <button key={o.key} type="button" onClick={() => setOperatingModel(o.key)} data-testid={`ob-operating-${o.key}`}
                  style={{ textAlign: "left", border: "2px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "#EAF2FF" : "#fff", borderRadius: 12, padding: "12px 14px", cursor: "pointer" }}>
                  <strong style={{ color: "var(--navy)", fontSize: 14 }}>{o.label}</strong>
                  <div className="sr-muted" style={{ fontSize: 12 }}>{o.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* What does this business promote? — hidden for Movie Distribution (always a movie) */}
      {askPromote && (
        <div className="sr-field" data-testid="ob-promote-types" style={{ marginTop: 10 }}>
          <label className="sr-label">What does this business promote? <span className="sr-muted" style={{ fontWeight: 400 }}>(pick all that apply)</span></label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
            {PROMOTE.map((p) => {
              const on = promoteTypes.includes(p.key);
              return (
                <button key={p.key} type="button" onClick={() => togglePromote(p.key)} data-testid={`ob-promote-${p.key}`}
                  style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {on ? "✓ " : ""}{p.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Movie-distributor capture — shown when the industry is Movie Distribution. Slim here;
          poster / showtimes / budget / AI plan are completed later in the Movie Studio. */}
      {isMovieDist && (
        <MoviePromoCapture compact initial={moviePromo || {}} onState={setMoviePromo} />
      )}

      <div style={{ height: 1, background: "var(--border)", margin: "18px 0" }} />

      {/* Identity capture — adapts to operating model */}
      {online ? (
        <div>
          <p className="sr-muted" style={{ marginBottom: 10 }}>{isMovieDist ? "Import your studio / distributor website to prefill the basics — you can review and edit everything." : "Import your website to prefill the basics — you can review and edit everything."}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <input className="sr-input" value={wsUrl} onChange={(e) => setWsUrl(e.target.value)} placeholder="yourstore.com" onKeyDown={(e) => e.key === "Enter" && importSite()} data-testid="ob-website-url" />
            <button className="sr-btn sr-btn-primary" onClick={importSite} disabled={wsBusy} data-testid="ob-website-import">
              {wsBusy ? <Loader2 size={16} className="sr-spin" /> : "Import"}
            </button>
          </div>
          {!picked && (
            <button className="sr-btn sr-btn-ghost" style={{ marginTop: 12 }} onClick={() => setPicked({ name: "", source: "manual" })} data-testid="ob-manual">
              Enter details manually
            </button>
          )}
        </div>
      ) : (
        <div>
          <p className="sr-muted" style={{ marginBottom: 12 }}>Enter your business name — we'll find it on Google and prefill the rest.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <input className="sr-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Sunrise Bistro Dallas"
              onKeyDown={(e) => e.key === "Enter" && search()} data-testid="ob-business-search" />
            <button className="sr-btn sr-btn-primary" onClick={search} disabled={searching} data-testid="ob-search-btn">
              {searching ? <Loader2 size={16} className="sr-spin" /> : <Search size={16} />}
            </button>
          </div>
          {note && <p className="sr-muted" style={{ marginTop: 10, fontSize: 13 }}>{note}</p>}
          {results.length > 0 && (
            <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
              {results.map((p) => (
                <button key={p.place_id} className="sr-card" onClick={() => pick(p)} data-testid={`ob-result-${p.place_id}`}
                  style={{ textAlign: "left", cursor: "pointer", padding: 14, display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <MapPin size={16} color="var(--blue)" style={{ marginTop: 3 }} />
                  <div><strong style={{ color: "var(--navy)" }}>{p.name}</strong>
                    <div className="sr-muted" style={{ fontSize: 13 }}>{p.address}{p.category ? ` · ${p.category}` : ""}</div></div>
                </button>
              ))}
            </div>
          )}
          {!picked && !results.length && (
            <button className="sr-btn sr-btn-ghost" style={{ marginTop: 14 }} onClick={() => setPicked({ name: q, source: "manual" })} data-testid="ob-manual">
              Enter details manually
            </button>
          )}
        </div>
      )}

      {picked && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="ob-picked">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="sr-badge sr-badge-green"><Check size={12} /> {online ? "Imported" : "Selected"}</span>
            {picked.source === "google_places" && <span className="sr-badge sr-badge-gray">Source: Google</span>}
            {picked.source === "website_import" && <span className="sr-badge sr-badge-gray">Source: Website</span>}
          </div>
          <div className="sr-field" style={{ marginTop: 12 }}><label className="sr-label">Business name</label>
            <input className="sr-input" value={picked.name || ""} onChange={(e) => setPicked({ ...picked, name: e.target.value })} data-testid="ob-name" /></div>
          {!online && (
            <div className="sr-field"><label className="sr-label">Address</label>
              <input className="sr-input" value={picked.address || ""} onChange={(e) => setPicked({ ...picked, address: e.target.value })} /></div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label">Category</label>
              <input className="sr-input" value={picked.category || ""} onChange={(e) => setPicked({ ...picked, category: e.target.value })} /></div>
            <div className="sr-field"><label className="sr-label">Your role</label>
              <select className="sr-select" value={role} onChange={(e) => setRole(e.target.value)} data-testid="ob-role">
                <option value="owner">Owner</option><option value="marketer">Marketer</option><option value="agency">Agency</option>
              </select></div>
          </div>
          {online && (
            <div className="sr-field"><label className="sr-label">Website</label>
              <input className="sr-input" value={picked.website || wsUrl} onChange={(e) => setPicked({ ...picked, website: e.target.value })} data-testid="ob-website-field" /></div>
          )}
          <div className="sr-field"><label className="sr-label">Phone</label>
            <input className="sr-input" value={picked.phone || ""} onChange={(e) => setPicked({ ...picked, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="ob-phone" /></div>

          {!online && (
            <div className="sr-field" data-testid="ob-owner-type">
              <label className="sr-label">How many locations do you run?</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[{ key: "single_unit", label: "Single location" }, { key: "multi_unit", label: "Multiple stores" }, { key: "franchisor", label: "Franchisor / brand" }].map((o) => {
                  const on = ownerType === o.key;
                  return (
                    <button key={o.key} type="button" onClick={() => setOwnerType(o.key)} data-testid={`ob-owner-type-${o.key}`}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Multi-store and franchisor owners can add stores, target each one, and set brand look-and-feel in Stores &amp; Franchise.</p>
            </div>
          )}

          {showCommerce && (
            <div data-testid="ob-online-details" style={{ marginTop: 6, padding: 12, borderRadius: 10, background: "#F7FAFF", border: "1px solid #DCE6F5" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Online selling details</div>
              <div className="sr-field"><label className="sr-label">Where do you sell / serve? <span className="sr-muted" style={{ fontWeight: 400 }}>(countries or regions, comma-separated)</span></label>
                <input className="sr-input" value={territories} onChange={(e) => setTerritories(e.target.value)} placeholder="United States, Canada" data-testid="ob-territories" /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="sr-field"><label className="sr-label">Reporting currency</label>
                  <input className="sr-input" value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="USD" data-testid="ob-currency" /></div>
                <div className="sr-field"><label className="sr-label">Timezone</label>
                  <input className="sr-input" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="America/Chicago" data-testid="ob-timezone" /></div>
              </div>
              <div className="sr-field"><label className="sr-label">Where do conversions happen?</label>
                <select className="sr-select" value={conversionLoc} onChange={(e) => setConversionLoc(e.target.value)} data-testid="ob-conversion-loc">
                  {CONV_LOC.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select></div>
            </div>
          )}

          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "var(--navy)", cursor: "pointer", marginTop: 8 }} data-testid="ob-sms-consent-label">
            <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} data-testid="ob-sms-consent" style={{ marginTop: 3 }} />
            <span>I agree to receive account &amp; service text messages (SMS) from SmartReachAI at this number (reconnect &amp; billing alerts). Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See <a href="/sms-terms.html" target="_blank" rel="noreferrer">SMS Terms</a> &amp; <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>.</span>
          </label>
        </div>
      )}

      <Nav onNext={cont} nextLabel={saving ? "Saving..." : "Continue"} disabled={saving} />
    </div>
  );
}

function Step2({ data, onBack, onNext, schema }) {
  const wf = schema?.workflows?.[data.industry] || schema?.workflows?.default || {};
  const isMovie = data.industry === "movie-distribution";
  const mp = data.movie_promo || {};
  const releaseType = mp.fields?.release_type || "theatrical";
  const salesModel = mp.mode;
  let goals = (wf.goals && wf.goals.length) ? wf.goals : GOALS;
  let contextLabel = wf.industry_label ? `Goals tailored for ${wf.industry_label}. ` : "";
  if (isMovie && schema?.release_type_goals) {
    const key = salesModel === "brand" ? "brand" : releaseType;
    goals = schema.release_type_goals[key] || schema.release_type_goals.theatrical || goals;
    const rtl = salesModel === "brand" ? "a brand / slate push" : `a ${releaseType} release`;
    contextLabel = `Goals tailored for ${rtl}. `;
  }
  const [goal, setGoal] = useState(data.goal || (isMovie ? goals[0]?.key : wf.primary_goal) || "");
  const [secondary, setSecondary] = useState(data.secondary || []);
  const toggle = (k) => setSecondary(secondary.includes(k) ? secondary.filter((x) => x !== k) : [...secondary, k]);
  return (
    <div>
      <p className="sr-muted" style={{ marginBottom: 16 }}>
        {contextLabel}Pick one primary goal. Add optional secondary goals.
      </p>
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
        {goals.map((g) => {
          const active = goal === g.key;
          return (
            <button key={g.key} className="sr-card sr-card-hover" onClick={() => setGoal(g.key)} data-testid={`ob-goal-${g.key}`}
              style={{ cursor: "pointer", textAlign: "left", borderColor: active ? "var(--blue)" : "var(--border)", borderWidth: active ? 2 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong style={{ color: "var(--navy)" }}>{g.label}</strong>
                {active && <CheckCircle2 size={18} color="var(--blue)" />}
              </div>
            </button>
          );
        })}
      </div>
      {goal && (
        <div style={{ marginTop: 20 }}>
          <label className="sr-label">Secondary goals (optional)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {goals.filter((g) => g.key !== goal).map((g) => (
              <button key={g.key} className={`sr-badge ${secondary.includes(g.key) ? "sr-badge-green" : "sr-badge-gray"}`}
                style={{ cursor: "pointer", border: "none" }} onClick={() => toggle(g.key)}>{g.label}</button>
            ))}
          </div>
        </div>
      )}
      <Nav onBack={onBack} onNext={() => goal ? onNext({ goal, secondary }) : toast.error("Choose a primary goal")} />
    </div>
  );
}

function Step3({ data, onBack, onNext }) {
  const cur = useCurrency();
  const [e, setE] = useState({ average_sale: "", gross_margin_pct: "", monthly_budget: 3000, start_date: "", restricted: "",
    avg_customer_value: "", conversion_rate: "", annual_return_visits: "", ...data.economics });
  const set = (k) => (ev) => setE({ ...e, [k]: ev.target.value });
  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    const p = data.profile || {};
    if (!p.category && !p.name) return;
    sr.budgetSuggestion({ business: p.name || "", category: p.category || "", city: p.city || p.address || "" })
      .then((s) => setSuggestion(s)).catch(() => setSuggestion(null));
  }, [data.profile]);
  const cont = async () => {
    const cv = parseFloat(e.avg_customer_value || e.average_sale || "0") || 0;
    const rr = parseFloat(e.annual_return_visits || "0") || 0;
    try {
      await sr.profileSave({
        avg_customer_value: cv || undefined,
        conversion_rate: parseFloat(e.conversion_rate || "0") || undefined,
        annual_return_visits: rr || undefined,
      });
    } catch { /* non-blocking — also settable in Settings */ }
    onNext({ economics: e });
  };
  const ltv = (() => {
    const cv = parseFloat(e.avg_customer_value || e.average_sale || "0") || 0;
    const rr = parseFloat(e.annual_return_visits || "0") || 0;
    return cv && rr ? cv * rr * 5 : 0;
  })();
  return (
    <div>
      <p className="sr-muted" style={{ marginBottom: 16 }}>These protect your margins and power the forecast. Estimates are fine.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="sr-field"><label className="sr-label">Average sale ({cur.currency})</label>
          <input className="sr-input" type="number" value={e.average_sale} onChange={set("average_sale")} data-testid="ob-avg-sale" /></div>
        <div className="sr-field"><label className="sr-label">Gross margin (%)</label>
          <input className="sr-input" type="number" value={e.gross_margin_pct} onChange={set("gross_margin_pct")} /></div>
        <div className="sr-field"><label className="sr-label">Monthly budget ({cur.currency})</label>
          <input className="sr-input" type="number" value={cur.isLocal ? cur.toLocal(e.monthly_budget) : e.monthly_budget}
            onChange={(ev) => setE((prev) => ({ ...prev, monthly_budget: cur.isLocal ? Math.round(cur.toUSD(ev.target.value)) : ev.target.value }))} data-testid="ob-budget" />
          {cur.isLocal && <div className="sr-muted" style={{ fontSize: 11, marginTop: 4 }} data-testid="ob-budget-usd">≈ ${Number(e.monthly_budget || 0).toLocaleString()} USD billed</div>}
          {suggestion && (
            <button type="button" onClick={() => setE((prev) => ({ ...prev, monthly_budget: suggestion.suggested_budget }))}
              data-testid="ob-budget-suggestion" style={{
                display: "flex", gap: 8, alignItems: "flex-start", width: "100%", textAlign: "left",
                marginTop: 8, padding: "9px 12px", borderRadius: 10, cursor: "pointer",
                background: "#EEF4FF", border: "1px solid #C7D7FE", color: "var(--navy)",
              }}>
              <Sparkles size={15} color="var(--blue)" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 12.5, lineHeight: 1.5, flex: 1 }}>
                <span>Suggested for a {suggestion.label}: <strong>{cur.money(suggestion.suggested_budget)}/mo</strong> — tap to use</span>
                <span style={{ display: "block", marginTop: 2 }} className="sr-muted" data-testid="ob-budget-range">{cur.isLocal ? `≈ ${cur.money(suggestion.suggested_budget)}/mo` : suggestion.range_label}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }} data-testid="ob-budget-confidence">
                  <span style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: suggestion.confidence === "high" ? "#12B76A" : suggestion.confidence === "medium" ? "#F79009" : "#98A2B3" }} />
                  <span className="sr-muted" style={{ fontSize: 11 }}>{suggestion.confidence_note}</span>
                </span>
              </span>
            </button>
          )}
        </div>
        <div className="sr-field"><label className="sr-label">Preferred start</label>
          <DateField value={e.start_date} onChange={(v) => setE({ ...e, start_date: v })} placeholder="Pick a start date" minToday testId="ob-start-date" /></div>
      </div>
      <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)", marginTop: 4 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Customer value &amp; lifetime value</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Avg value per customer ($)</label>
            <input className="sr-input" type="number" value={e.avg_customer_value} onChange={set("avg_customer_value")} placeholder={e.average_sale || "45"} data-testid="ob-customer-value" /></div>
          <div className="sr-field"><label className="sr-label">Visit → customer rate (%)</label>
            <input className="sr-input" type="number" value={e.conversion_rate} onChange={set("conversion_rate")} placeholder="8" data-testid="ob-conversion" /></div>
          <div className="sr-field"><label className="sr-label">Return visits / year</label>
            <input className="sr-input" type="number" value={e.annual_return_visits} onChange={set("annual_return_visits")} placeholder="4" data-testid="ob-return-visits" /></div>
        </div>
        <p className="sr-muted" style={{ fontSize: 12, margin: 0 }}>
          How often a customer comes back tells us their 5-year value.{ltv ? ` A customer is worth ~$${ltv.toLocaleString()} over 5 years.` : ""}
        </p>
      </div>
      <div className="sr-field" style={{ marginTop: 14 }}><label className="sr-label">Restricted products / claims (optional)</label>
        <input className="sr-input" value={e.restricted} onChange={set("restricted")} placeholder="Anything we should avoid" /></div>
      <div className="sr-card" style={{ marginTop: 14, background: "var(--surface)" }}>
        <GeographyPicker compact />
        <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 10, marginBottom: 0 }}>
          This shapes which channels we offer — for example, on-air radio is available in Dallas–Fort Worth (FunAsia) and Dubai / UAE.
        </p>
      </div>
      <Nav onBack={onBack} onNext={cont} />
    </div>
  );
}

function Step4({ data, onBack, onNext }) {
  const [mode, setMode] = useState(data.service_mode || "SELF_SERVICE");
  const [authority, setAuthority] = useState(data.ai_authority || "RECOMMEND_ONLY");
  const Card = ({ item, active, onClick, tid }) => (
    <button className="sr-card sr-card-hover" onClick={onClick} data-testid={tid}
      style={{ cursor: "pointer", textAlign: "left", borderColor: active ? "var(--blue)" : "var(--border)", borderWidth: active ? 2 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <strong style={{ color: "var(--navy)" }}>{item.label}</strong>{active && <CheckCircle2 size={18} color="var(--blue)" />}
      </div>
      <p className="sr-muted" style={{ marginTop: 6, fontSize: 14 }}>{item.desc}</p>
    </button>
  );
  return (
    <div>
      <p className="sr-muted" style={{ marginBottom: 12 }}>You can switch modes anytime — nothing is recreated.</p>
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
        {MODES.map((m) => <Card key={m.key} item={m} active={mode === m.key} onClick={() => setMode(m.key)} tid={`ob-mode-${m.key}`} />)}
      </div>
      <label className="sr-label" style={{ marginTop: 22 }}>How much authority should AI have?</label>
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
        {AUTH.map((a) => <Card key={a.key} item={a} active={authority === a.key} onClick={() => setAuthority(a.key)} tid={`ob-auth-${a.key}`} />)}
      </div>
      <Nav onBack={onBack} onNext={() => onNext({ service_mode: mode, ai_authority: authority })} />
    </div>
  );
}

const STATUS_UI = {
  managed: { label: "Managed by SmartReachAI", cls: "sr-badge-green", icon: CheckCircle2 },
  ops_setup: { label: "Setup on request", cls: "sr-badge-amber", icon: Plug },
  requested: { label: "Setup requested", cls: "sr-badge-green", icon: Check },
  coming_soon: { label: "Coming soon", cls: "sr-badge-gray", icon: null },
};

function GAConnect() {
  const [status, setStatus] = useState(null);
  const [props, setProps] = useState(null);
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState(null);
  const load = () => sr.ga4Status().then(setStatus).catch(() => setStatus({ configured: false, connected: false }));
  useEffect(() => {
    load();
    const ga = new URLSearchParams(window.location.search).get("ga");
    if (ga === "connected") toast.success("Google Analytics connected");
    if (ga === "error") toast.error("Google Analytics connection failed — please try again");
    if (ga) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  useEffect(() => {
    if (status?.connected && !status?.property_id) sr.ga4Properties().then((r) => setProps(r.properties || [])).catch(() => setProps([]));
    if (status?.connected && status?.property_id) sr.ga4Report().then(setReport).catch(() => {});
  }, [status?.connected, status?.property_id]);

  const connect = async () => {
    setBusy("connect");
    try { const r = await sr.ga4Connect(); window.location.assign(r.authorization_url); }
    catch (e) { toast.error(e?.response?.data?.detail || "Google Analytics isn't available yet"); setBusy(""); }
  };
  const selectProperty = async (id, name) => {
    setBusy("select");
    try { await sr.ga4SelectProperty(id, name); toast.success("Analytics property connected"); setProps(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not select property"); } finally { setBusy(""); }
  };
  const disconnect = async () => {
    setBusy("disc");
    try { await sr.ga4Disconnect(); setReport(null); setProps(null); await load(); toast.success("Google Analytics disconnected"); }
    catch { toast.error("Could not disconnect"); } finally { setBusy(""); }
  };

  if (!status) return null;
  return (
    <div className="sr-card" style={{ marginBottom: 14, borderLeft: "3px solid #E8710A" }} data-testid="ga-connect-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong style={{ color: "var(--navy)" }}>Connect Google Analytics</strong>
          <div className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>Link your Google Analytics so your growth plan uses your real website traffic. You can revisit Setup anytime to connect.</div>
        </div>
        {status.connected ? (
          <span className="sr-badge sr-badge-green" data-testid="ga-status"><CheckCircle2 size={12} /> Connected</span>
        ) : (
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={connect} disabled={busy === "connect" || !status.configured} data-testid="ga-connect-btn">
            {busy === "connect" ? <Loader2 size={13} className="sr-spin" /> : "Connect Google Analytics"}
          </button>
        )}
      </div>
      {!status.configured && !status.connected && (
        <p className="sr-muted" style={{ fontSize: 12, marginTop: 10 }} data-testid="ga-not-configured">Google Analytics will be enabled for your workspace shortly — check back soon.</p>
      )}
      {status.connected && !status.property_id && props && (
        <div style={{ marginTop: 12 }} data-testid="ga-property-picker">
          <label className="sr-label">Choose your Analytics property</label>
          {props.length === 0 ? (
            <p className="sr-muted" style={{ fontSize: 13 }}>No Analytics properties found for this Google account.</p>
          ) : (
            <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
              {props.map((p) => (
                <button key={p.id} className="sr-btn sr-btn-ghost sr-btn-sm" style={{ justifyContent: "space-between" }}
                  onClick={() => selectProperty(p.id, p.display_name)} disabled={busy === "select"} data-testid={`ga-property-${p.id}`}>
                  {p.display_name || p.name} <span className="sr-muted">#{p.id}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {status.connected && status.property_id && (
        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div className="sr-muted" style={{ fontSize: 13 }} data-testid="ga-report">
            {report ? <>Last 28 days: <strong style={{ color: "var(--navy)" }}>{Number(report.summary?.sessions || 0).toLocaleString()}</strong> sessions · <strong style={{ color: "var(--navy)" }}>{Number(report.summary?.users || 0).toLocaleString()}</strong> users</> : <>Property {status.property_name || status.property_id} connected.</>}
          </div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={disconnect} disabled={busy === "disc"} data-testid="ga-disconnect">Disconnect</button>
        </div>
      )}
    </div>
  );
}

function PixelCard() {
  const [px, setPx] = useState(null);
  const [show, setShow] = useState(false);
  const [rows, setRows] = useState([]);
  const [savingConv, setSavingConv] = useState(false);
  useEffect(() => {
    sr.getPixel().then((p) => {
      setPx(p);
      setRows((p.conversion_paths || []).map((c) => ({ path: c.path, value: c.value || 0 })));
    }).catch(() => {});
  }, []);
  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const addRow = () => setRows((r) => [...r, { path: "", value: 0 }]);
  const delRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const saveConv = async () => {
    setSavingConv(true);
    try {
      const conversions = rows.map((r) => ({ path: (r.path || "").trim(), value: Number(r.value) || 0 })).filter((r) => r.path);
      const res = await sr.setPixelConversions(conversions);
      setPx((p) => ({ ...p, conversion_paths: res.conversion_paths, has_conversion_value: res.conversion_paths.some((c) => c.value > 0), stats: { ...p.stats, conversions: res.conversions, revenue: res.revenue } }));
      setRows(res.conversion_paths.map((c) => ({ path: c.path, value: c.value || 0 })));
      toast.success("Conversion pages saved");
    } catch { toast.error("Could not save conversion pages"); } finally { setSavingConv(false); }
  };
  if (!px) return null;
  const rev = px.stats?.revenue || 0;
  return (
    <div className="sr-card" style={{ marginBottom: 14, borderLeft: "3px solid var(--blue)" }} data-testid="setup-pixel-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong style={{ color: "var(--navy)" }}>Your website pixel</strong>
          <div className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>Install it once on your site so campaigns can build lookalike audiences and measure conversions. {px.installed ? <span style={{ color: "var(--success)", fontWeight: 700 }}>✓ Active — {px.stats.page_views.toLocaleString()} views · {(px.stats.conversions || 0).toLocaleString()} conversions{rev > 0 ? ` · $${rev.toLocaleString()}` : ""}</span> : "Not detected yet."}</div>
        </div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setShow((v) => !v)} data-testid="setup-pixel-toggle">{show ? "Hide" : "Show"} snippet</button>
      </div>
      {show && (
        <div style={{ marginTop: 10 }}>
          <pre style={{ background: "#0f172a", color: "#e2e8f0", padding: 10, borderRadius: 6, fontSize: 11, overflowX: "auto", whiteSpace: "pre-wrap" }} data-testid="setup-pixel-snippet">{px.snippet}</pre>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { navigator.clipboard?.writeText(px.snippet); toast.success("Pixel snippet copied"); }} data-testid="setup-pixel-copy">Copy snippet</button>
        </div>
      )}
      <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }} data-testid="setup-conversion-pages">
        <label className="sr-label">Conversion pages <span className="sr-muted" style={{ fontWeight: 400 }}>· mark the pages that count as a sale/lead and their value</span></label>
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input className="sr-input" value={r.path} onChange={(e) => setRow(i, "path", e.target.value)} placeholder="/checkout" data-testid={`setup-conv-path-${i}`} style={{ flex: 1, minWidth: 180 }} />
              <span className="sr-muted">$</span>
              <input className="sr-input" type="number" min="0" step="0.01" value={r.value} onChange={(e) => setRow(i, "value", e.target.value)} placeholder="0" data-testid={`setup-conv-value-${i}`} style={{ width: 100 }} />
              <span className="sr-muted" style={{ fontSize: 12 }}>per conversion</span>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => delRow(i)} data-testid={`setup-conv-del-${i}`}><X size={14} /></button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={addRow} data-testid="setup-conv-add"><Plus size={14} /> Add page</button>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveConv} disabled={savingConv} data-testid="setup-conversion-save">{savingConv ? "Saving…" : "Save"}</button>
        </div>
        <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Any page whose URL contains one of these counts as a conversion; the dollar value lets your uplift show revenue.</p>
      </div>
    </div>
  );
}

function RecapCard() {
  const [st, setSt] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => { sr.trafficRecapGet().then((s) => { setSt(s); setRecipient(s.recipient || ""); }).catch(() => setSt({ enabled: false })); }, []);
  const save = async (enabled) => {
    setBusy("save");
    try { const s = await sr.trafficRecapSet({ enabled, recipient: recipient.trim() || null }); setSt(s); toast.success(enabled ? "Weekly recap on" : "Weekly recap off"); }
    catch { toast.error("Could not save"); } finally { setBusy(""); }
  };
  const sendNow = async () => {
    setBusy("send");
    try { const r = await sr.trafficRecapSend(recipient.trim() || null); toast.success(`Recap sent to ${r.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send recap"); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    setBusy("preview");
    try { const p = await sr.trafficRecapPreview(); setPreview(p); }
    catch { toast.error("Could not build preview"); } finally { setBusy(""); }
  };
  if (!st) return null;
  return (
    <div className="sr-card" style={{ marginBottom: 14, borderLeft: "3px solid #067647" }} data-testid="setup-recap-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong style={{ color: "var(--navy)" }}>Weekly recap email</strong>
          <div className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>Get a short Monday email with your website views, conversions and each campaign's uplift.</div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={!!st.enabled} onChange={(e) => save(e.target.checked)} disabled={busy === "save"} data-testid="setup-recap-toggle" />
          <span style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>{st.enabled ? "On" : "Off"}</span>
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input className="sr-input" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Send to (defaults to your email)" data-testid="setup-recap-recipient" style={{ flex: 1, minWidth: 220 }} />
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => save(st.enabled)} disabled={busy === "save"} data-testid="setup-recap-save">Save</button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={doPreview} disabled={busy === "preview"} data-testid="setup-recap-preview">Preview</button>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={sendNow} disabled={busy === "send"} data-testid="setup-recap-send">{busy === "send" ? "Sending…" : "Send me one now"}</button>
      </div>
      {preview && (
        <div style={{ marginTop: 12 }} data-testid="setup-recap-preview-box">
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Preview · {preview.subject} {preview.no_activity && "· (no views yet this week)"}</div>
          <iframe title="recap-preview" srcDoc={preview.html} style={{ width: "100%", height: 320, border: "1px solid var(--border)", borderRadius: 8, background: "#060913" }} />
        </div>
      )}
    </div>
  );
}

function Step5({ data, onBack, onNext }) {
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => sr.capabilities().then((r) => setCaps(r.capabilities)).catch(() => setCaps([]));
  useEffect(() => { load(); }, []);
  const requestSetup = async (c) => {
    const key = `${c.connector_key}-${c.channel}`;
    setBusy(key);
    try {
      await sr.requestConnectorSetup(c.connector_key, c.channel, "");
      toast.success(`Requested — our team will set up ${c.display_name || c.channel}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); } finally { setBusy(""); }
  };
  if (!caps) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  return (
    <div>
      <GAConnect />
      <PixelCard />
      <RecapCard />
      <p className="sr-muted" style={{ marginBottom: 12 }}>Your Google Search (SEM) and social media are managed by SmartReachAI and their results flow in here — so you manage every digital campaign in one place. Nothing to connect.</p>
      <div style={{ display: "grid", gap: 10 }}>
        {caps.map((c) => {
          const st = STATUS_UI[c.status] || STATUS_UI.coming_soon;
          const key = `${c.connector_key}-${c.channel}`;
          const Icon = st.icon;
          return (
            <div key={key} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: 16 }} data-testid={`ob-cap-${key}`}>
              <div style={{ flex: 1 }}>
                <strong style={{ color: "var(--navy)" }}>{c.display_name || `${c.connector_key.replace(/_/g, " ")} · ${c.channel.replace(/_/g, " ")}`}</strong>
                {c.note && <div className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>{c.note}</div>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span className={`sr-badge ${st.cls}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`ob-cap-status-${key}`}>
                  {Icon && <Icon size={12} />} {st.label}
                </span>
                {c.action === "request_setup" && (
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => requestSetup(c)} disabled={busy === key} data-testid={`ob-cap-request-${key}`}>
                    {busy === key ? <Loader2 size={13} className="sr-spin" /> : "Request setup"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="sr-muted" style={{ fontSize: 12, marginTop: 12 }}>Nothing here blocks your draft plan — you can build it now and complete any setup before launch.</p>
      <Nav onBack={onBack} onNext={onNext} nextLabel="Continue" />
    </div>
  );
}

function Step6({ data, onBack, persist, nav, schema }) {
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState(MEDIA_CHANNELS.map((c) => c.key));
  const [brief, setBrief] = useState(data.brief || "");
  const wf = schema?.workflows?.[data.industry] || schema?.workflows?.default || {};
  const goalKey = data.goal || wf.primary_goal || "visits";
  const goalLabel = (wf.goals || []).find((g) => g.key === goalKey)?.label || schema?.goal_library?.[goalKey] || goalKey;
  const [targeting, setTargeting] = useState({ mode: "audience", conquesting: false, audienceIds: [], audienceNames: [], contextualIds: [], contextualNames: [] });
  const generate = async () => {
    if (!channels.length) { toast.error("Pick at least one channel"); return; }
    setBusy(true);
    try {
      const locs = await sr.locations();
      const r = await sr.generatePlan({
        goal: goalKey, secondary: data.secondary || [],
        monthly_budget: Number(data.economics?.monthly_budget) || 3000,
        location_id: locs.locations?.[0]?.id, channels,
        brief: brief.trim() || null,
        conquesting: !!targeting.conquesting,
        audience_segments: targeting.audienceIds,
        contextual_segments: targeting.contextualIds,
        targeting_summary: targetingSummary(targeting),
      });
      persist(6, {}, true);
      toast.success(r.ai_available ? "Your growth plan is ready" : "Draft plan created (AI was busy — refine before launch)");
      nav(`/app/media-mix?plan=${r.growth_plan.id}`);
    } catch { toast.error("Could not generate the plan"); } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="sr-muted" style={{ marginBottom: 18 }}>
        We'll build a complete draft plan — offer, audience, geography, channel mix, budget, creative, measurement
        and a forecast range with limitations. You approve what runs.
      </p>
      <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Sparkles size={22} color="var(--blue)" />
          <div>
            <strong style={{ color: "var(--navy)" }}>Goal: <span data-testid="ob-goal-label">{goalLabel}</span></strong>
            <div className="sr-muted" style={{ fontSize: 13 }}>Budget: ${Number(data.economics?.monthly_budget || 3000).toLocaleString()}/mo · Mode: {(data.service_mode || "SELF_SERVICE").replace(/_/g, " ")}</div>
          </div>
        </div>
      </div>
      {data.industry && schema?.playbooks?.[data.industry] && (
        <div style={{ marginTop: 16 }}>
          <PlaybookCard playbook={schema.playbooks[data.industry]} title="Your tailored starter plan" />
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <label className="sr-label" style={{ display: "block", marginBottom: 8 }}>Which media channels should the plan include?</label>
        <ChannelPicker value={channels} onChange={setChannels} testid="ob-channels" />
      </div>
      <div style={{ marginTop: 16 }}>
        <label className="sr-label" style={{ display: "block", marginBottom: 6 }}>
          What should this campaign achieve? <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea className="sr-input" rows={3} value={brief} onChange={(e) => setBrief(e.target.value)}
          data-testid="ob-brief"
          placeholder="e.g. Fill our Tuesday–Thursday lunch tables, target office workers within 2 miles, push our $12 lunch combo." />
        <div className="sr-muted" style={{ fontSize: 12, marginTop: 5 }}>
          Tell the AI in your own words — we'll tailor the offer, audience and creative to match.
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <AdvancedTargeting geo={null} value={targeting} onChange={setTargeting} />
      </div>
      <button className="sr-btn sr-btn-gold" style={{ marginTop: 22, fontSize: 16, padding: "14px 26px" }} onClick={generate} disabled={busy} data-testid="ob-generate-btn">
        {busy ? <><Loader2 size={18} className="sr-spin" /> Building your plan…</> : <>Build my growth plan <ArrowRight size={18} /></>}
      </button>
      <Nav onBack={onBack} />
    </div>
  );
}
